import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldAlert, ClipboardCheck, FileText, FolderOpen, Briefcase, Plus, ArrowRight, Loader2 } from "lucide-react";
import { api, fmtDate, SEVERITY_STYLES } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DashboardTour, tourStorageKey } from "@/components/app/DashboardTour";

const STATUS_COLORS = { "on track": "bg-emerald-500", "needs attention": "bg-amber-500", overdue: "bg-orange-500", critical: "bg-red-500" };

const KpiCard = ({ to, icon: Icon, label, value, testid, color = "text-navy" }) => (
  <Link to={to} className="rounded-lg border border-navy/10 bg-white p-5 transition-all hover:-translate-y-0.5 hover:border-gold" data-testid={testid}>
    <Icon className="h-5 w-5 text-golddark" />
    <p className={`mt-3 font-serif text-3xl font-semibold ${color}`} data-testid={`${testid}-value`}>{value}</p>
    <p className="mt-1 text-xs font-semibold uppercase tracking-wider text-navy/50">{label}</p>
  </Link>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [tourOpen, setTourOpen] = useState(false);

  useEffect(() => {
    api.get("/api/app/dashboard").then((r) => setData(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (data && !localStorage.getItem(tourStorageKey)) setTourOpen(true);
  }, [data]);

  const finishTour = () => {
    try {
      localStorage.setItem(tourStorageKey, "1");
    } catch {
      /* storage unavailable */
    }
    setTourOpen(false);
  };

  if (!data) {
    return <div className="flex h-64 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-gold" /></div>;
  }

  const r = data.readiness;

  return (
    <div className="space-y-8" data-testid="customer-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="eyebrow">Dashboard</p>
          <h1 className="mt-3 font-serif text-4xl font-semibold text-navy" data-testid="dashboard-title">{user?.business_name}</h1>
        </div>
        <div className="flex flex-col items-end gap-1">
          <Link to="/app/billing" className="text-sm font-semibold text-golddark hover:text-navy" data-testid="dashboard-billing-link">
            {user?.subscription_status === "active" ? "MITIVISE Workspace · active" : "Upgrade your plan"} →
          </Link>
          <button type="button" onClick={() => setTourOpen(true)} data-testid="dashboard-tour-restart" className="text-xs font-medium text-ink/40 transition-colors hover:text-golddark">
            Replay the 60-second tour
          </button>
        </div>
      </div>

      <div className="grid gap-5 lg:grid-cols-3" data-testid="dashboard-readiness">
        <div className="noise-dark rounded-lg bg-navy p-7 text-white lg:col-span-1">
          <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-gold">Compliance Readiness Indicator</p>
          <p className="mt-4 font-serif text-6xl font-semibold" data-testid="readiness-score">{r.score}<span className="text-2xl text-white/50">/100</span></p>
          <div className="mt-5 space-y-2.5">
            {r.components.map((c) => (
              <div key={c.label}>
                <div className="flex justify-between text-[11px] text-white/60">
                  <span>{c.label}</span><span>{c.value}%</span>
                </div>
                <div className="mt-1 h-1.5 rounded-full bg-white/10">
                  <div className="h-full rounded-full bg-gold" style={{ width: `${c.value}%` }} />
                </div>
              </div>
            ))}
          </div>
          <p className="mt-5 border-t border-white/10 pt-3 text-[10px] italic leading-relaxed text-white/40">{r.disclaimer}</p>
        </div>

        <div className="grid gap-5 sm:grid-cols-2 lg:col-span-2">
          <KpiCard to="/app/risks" icon={ShieldAlert} label="Open risks (Critical)" value={data.risks_by_severity.Critical} testid="kpi-critical-risks" color="text-red-600" />
          <KpiCard to="/app/audits" icon={ClipboardCheck} label="Upcoming / open audits" value={data.upcoming_audits.length} testid="kpi-audits" />
          <KpiCard to="/app/documents" icon={FolderOpen} label="Documents expiring ≤60 days" value={data.expiring_documents.length} testid="kpi-expiring-docs" color="text-amber-600" />
          <KpiCard to="/app/sops" icon={FileText} label="SOPs needing review" value={data.sops_needing_review_count} testid="kpi-sops-review" color="text-amber-600" />
        </div>
      </div>

      <div className="flex flex-wrap gap-3" data-testid="dashboard-quick-actions">
        {[
          { to: "/app/risks?new=1", label: "Add a Risk", tid: "qa-add-risk" },
          { to: "/app/audits?new=1", label: "Log an Audit", tid: "qa-log-audit" },
          { to: "/app/sops?new=1", label: "Create an SOP", tid: "qa-create-sop" },
          { to: "/app/documents?new=1", label: "Upload a Document", tid: "qa-upload-doc" },
          { to: "/app/services", label: "Request a TSM Service", tid: "qa-request-service" },
        ].map((a) => (
          <Link key={a.tid} to={a.to} data-testid={a.tid}
                className="inline-flex items-center gap-2 rounded-full border border-navy/15 bg-white px-5 py-2.5 text-sm font-semibold text-navy transition-all hover:-translate-y-0.5 hover:border-gold">
            <Plus className="h-4 w-4 text-golddark" />
            {a.label}
          </Link>
        ))}
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-lg border border-navy/10 bg-white p-6" data-testid="dashboard-expiring-docs">
          <h2 className="font-serif text-2xl font-semibold text-navy">Expiring & expiring-soon documents</h2>
          {data.expiring_documents.length === 0 ? (
            <p className="mt-4 text-sm text-ink/50">Nothing expiring in the next 60 days. Upload documents to track them automatically.</p>
          ) : (
            <ul className="mt-4 space-y-3">
              {data.expiring_documents.map((d) => (
                <li key={d.id} className="flex items-center justify-between gap-4 border-b border-navy/5 pb-3 text-sm">
                  <div>
                    <p className="font-semibold text-navy">{d.name}</p>
                    <p className="text-xs text-ink/50">{d.category} · {fmtDate(d.expiration_date)}</p>
                  </div>
                  <span className={`rounded-full border px-3 py-1 text-xs font-semibold ${d.days_left < 0 ? "bg-red-50 text-red-700 border-red-200" : d.days_left <= 14 ? "bg-orange-50 text-orange-700 border-orange-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                    {d.days_left < 0 ? `${Math.abs(d.days_left)} days overdue` : `${d.days_left} days left`}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="rounded-lg border border-navy/10 bg-white p-6" data-testid="dashboard-overdue">
          <h2 className="font-serif text-2xl font-semibold text-navy">Overdue actions & missing documents</h2>
          <div className="mt-4 space-y-2 text-sm">
            {data.overdue_actions.length === 0 && data.missing_document_categories.length === 0 && data.overdue_audits_count === 0 ? (
              <p className="text-ink/50">Nothing overdue — on track.</p>
            ) : (
              <>
                {data.overdue_audits_count > 0 && (
                  <p className="flex items-center gap-2 text-orange-700" data-testid="dashboard-overdue-audits">
                    <span className={`h-2 w-2 rounded-full ${STATUS_COLORS.overdue}`} />
                    {data.overdue_audits_count} audit(s) past due date
                  </p>
                )}
                {data.overdue_actions.map((a) => (
                  <p key={a.risk_id} className="flex items-start gap-2 text-orange-700">
                    <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${STATUS_COLORS.overdue}`} />
                    <span><span className="font-semibold">{a.risk_id}</span> — action plan past target date {fmtDate(a.target_date)}</span>
                  </p>
                ))}
                {data.missing_document_categories.map((c) => (
                  <p key={c} className="flex items-center gap-2 text-amber-700" data-testid="dashboard-missing-doc">
                    <span className={`h-2 w-2 rounded-full ${STATUS_COLORS["needs attention"]}`} />
                    No {c.toLowerCase()} on file yet
                  </p>
                ))}
              </>
            )}
          </div>
          <h3 className="mt-6 font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-navy/50">Open TSM service requests</h3>
          <p className="mt-2 text-sm text-navy" data-testid="dashboard-open-requests">{data.open_service_requests}</p>
          <Link to="/app/services" className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-golddark hover:text-navy" data-testid="dashboard-services-link">
            TSM Services Marketplace <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>

      <div className="rounded-lg border border-navy/10 bg-white p-6" data-testid="dashboard-activity">
        <h2 className="font-serif text-2xl font-semibold text-navy">Recent activity</h2>
        <ul className="mt-4 space-y-2 text-sm text-ink/70">
          {data.recent_activity.length === 0 && <li className="text-ink/50">Activity will appear as you use MITIVISE.</li>}
          {data.recent_activity.map((a, i) => (
            <li key={i} className="flex items-center gap-3 border-b border-navy/5 pb-2 last:border-0">
              <span className="h-1.5 w-1.5 shrink-0 rotate-45 bg-gold" />
              <span className="capitalize">{a.action?.replace(/_/g, " ")}</span>
              <span className="ml-auto text-xs text-ink/40">{a.created_at ? new Date(a.created_at).toLocaleDateString() : ""}</span>
            </li>
          ))}
        </ul>
      </div>

      <DashboardTour open={tourOpen} onDone={finishTour} />
    </div>
  );
}
